import { action, computed, observable } from 'mobx'
import { task } from 'mobx-task'
import { func, pattern, required, validationContext } from 'validx'
import type {
  GetCurrentMemberFn,
  UpdateMemberFn,
  UpdateUserDataFn,
} from '@/features/iam'
import type { UserDataType } from '@/features/iam/users/UserData'
import { SearchableUser } from '@/features/iam/users/SearchableUser'
import { Workspace } from '@/features/iam/workspaces/Workspace'
import type { AlertErrorHandlerFactory, AlertFactory } from '@/features/shell'
import { ModalViewModel } from '@/infra/ModalViewModel'

interface UserTypeOption {
  title: string
  value: UserDataType
}

export class EditUserInformationDialogViewModel {
  readonly modal = new ModalViewModel()

  @observable
  accessor currentUser: SearchableUser = SearchableUser.none

  @observable
  accessor givenName = ''

  @observable
  accessor familyName = ''

  @observable
  accessor email = ''

  @observable
  accessor phone = ''

  @observable
  accessor type: UserDataType = 'User'

  @observable
  accessor workspace: Workspace = Workspace.none

  userTypeOptions: UserTypeOption[] = [
    {
      title: 'Admin',
      value: 'Admin',
    },
    {
      title: 'User',
      value: 'User',
    },
    {
      title: 'Pro',
      value: 'Pro',
    },
    {
      title: 'Call Agent',
      value: 'CallAgent',
    },
  ]

  validation = validationContext(this, {
    givenName: [
      required(),
      func(({ value }: { value: string }) => {
        return (
          value.length <= 40 || "First name can't be more than 40 characters"
        )
      }),
    ],
    familyName: [
      required(),
      func(({ value }: { value: string }) => {
        return (
          value.length <= 40 || "Last name can't be more than 40 characters"
        )
      }),
    ],
    email: [
      required(),
      pattern('email'),
      func(({ value }: { value: string }) => {
        return value.length <= 320 || "Email can't be more than 320 characters"
      }),
    ],
    phone: [
      required(),
      func(
        (ctx) =>
          !ctx.value ||
          pattern(
            /^[+]?[ 0-9()-]+$/i,
            'This does not look like a phone number.',
          )(ctx),
      ),
      func(({ value }: { value: string }) => {
        return value.length <= 20 || "Phone can't be more than 20 characters"
      }),
    ],
  })

  constructor(
    private readonly _onSaved: () => Promise<void>,
    private readonly _updateMember: UpdateMemberFn,
    private readonly _updateUserData: UpdateUserDataFn,
    private readonly _getCurrentMember: GetCurrentMemberFn,
    private readonly _newAlert: AlertFactory,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
  ) {}

  @action.bound
  open(currentUser: SearchableUser, workspace: Workspace) {
    this.currentUser = currentUser
    this.givenName = currentUser.givenName
    this.familyName = currentUser.familyName
    this.email = currentUser.email
    this.phone = currentUser.phone
    this.type = currentUser.userDataType
    this.workspace = workspace

    this.modal.show()
  }

  @computed
  get selectedUserType() {
    return this.userTypeOptions.find((i) => i.value === this.type)
  }

  @computed
  get isPlatformAdmin() {
    return this._getCurrentMember().user.platformAdmin
  }

  @action.bound
  setUserType(userType?: UserTypeOption) {
    if (userType) {
      this.type = userType.value
    }
  }

  submit = task.resolved(async () => {
    if (!this.validation.reset().validate().isValid) {
      return
    }

    const alert = this._newAlert()
      .withState('pending')
      .saying('Saving user data!')
      .autoDismiss()
      .show()

    try {
      await this._updateMember({
        workspaceId: this.workspace.id,
        userPublicId: this.currentUser.publicId,
        familyName: this.familyName,
        givenName: this.givenName,
        phone: this.phone,
        email: this.email,
      })
      if (this.isPlatformAdmin) {
        await this._updateUserData(this.currentUser.publicId, {
          type: this.type,
        })
      }
    } catch (err) {
      alert.dismiss()
      this._createAlertErrorHandler('Saving user data failed')(err)
    }

    // The save succeeded, so a failed refresh still reports success.
    await this._onSaved().catch(() => undefined)

    alert
      .withState('success')
      .saying('Success!', 'User data saved successfully!')
      .autoDismiss()
      .show()

    this.modal.close()
  })
}
